import { DIR_VECTORS, formatHexId, parseHexId } from "../data/board-geometry";
import type { BoardDefinition } from "../types/game-data-config";

export function getNeighbors(
  boardDef: BoardDefinition,
  hexKey: string,
): readonly string[] {
  return boardDef.adjacency[hexKey] ?? [];
}

export function areAdjacent(
  boardDef: BoardDefinition,
  a: string,
  b: string,
): boolean {
  return (boardDef.adjacency[a] ?? []).includes(b);
}

export function isLocation(boardDef: BoardDefinition, hexKey: string): boolean {
  return boardDef.locations.includes(hexKey);
}

export function isValidHex(boardDef: BoardDefinition, hexKey: string): boolean {
  return boardDef.hexes.includes(hexKey);
}

// BFS shortest path distance between two hexes using the board adjacency map
export function getHexDistance(
  boardDef: BoardDefinition,
  a: string,
  b: string,
): number {
  if (a === b) return 0;
  const visited = new Set<string>([a]);
  let frontier = [a];
  let dist = 0;
  while (frontier.length > 0) {
    dist++;
    const next: string[] = [];
    for (const hex of frontier) {
      for (const neighbor of boardDef.adjacency[hex] ?? []) {
        if (neighbor === b) return dist;
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          next.push(neighbor);
        }
      }
    }
    frontier = next;
    if (dist > 100) return Infinity; // safety
  }
  return Infinity;
}

// Returns the direction vector if `from` and `to` are adjacent via one of the 6 DIR_VECTORS.
// Returns null if they are not adjacent, or not connected by a single direction.
export function getDirectionVector(
  from: string,
  to: string,
): readonly [number, number] | null {
  const [fc, fr] = parseHexId(from);
  const [tc, tr] = parseHexId(to);
  const dc = tc - fc;
  const dr = tr - fr;
  for (const v of Object.values(DIR_VECTORS)) {
    if (Math.abs(v[0] - dc) < 0.001 && Math.abs(v[1] - dr) < 0.001) return v;
  }
  return null;
}

// Returns the hex ID after moving `steps` times in direction [dc, dr] from startId.
// Does NOT check if the hex is on the board.
export function getHexInDirection(
  startId: string,
  dir: readonly [number, number],
  steps: number,
): string {
  const [col, row] = parseHexId(startId);
  return formatHexId(col + dir[0] * steps, row + dir[1] * steps);
}

// Returns all hexes reachable from startHex in exactly `range` steps (BFS layer)
export function getHexesAtExactRange(
  boardDef: BoardDefinition,
  startHex: string,
  range: number,
): readonly string[] {
  if (range === 0) return [startHex];
  const visited = new Set<string>([startHex]);
  let frontier = [startHex];
  for (let d = 0; d < range; d++) {
    const next: string[] = [];
    for (const hex of frontier) {
      for (const nb of boardDef.adjacency[hex] ?? []) {
        if (!visited.has(nb)) {
          visited.add(nb);
          next.push(nb);
        }
      }
    }
    frontier = next;
  }
  return frontier; // hexes exactly `range` steps away
}

// Returns all hexes reachable from startHex in up to `range` steps (BFS, including start)
export function getHexesWithinRange(
  boardDef: BoardDefinition,
  startHex: string,
  range: number,
): readonly string[] {
  const visited = new Set<string>([startHex]);
  let frontier = [startHex];
  for (let d = 0; d < range; d++) {
    const next: string[] = [];
    for (const hex of frontier) {
      for (const nb of boardDef.adjacency[hex] ?? []) {
        if (!visited.has(nb)) {
          visited.add(nb);
          next.push(nb);
        }
      }
    }
    frontier = next;
  }
  return [...visited];
}
